import { loadUserPrefs } from '../utils/userPrefs';

const SUPPORTED = new Set(['en', 'no', 'de', 'fr', 'es', 'ro']);

const TRANSLATIONS = {
  en: {
    'home.recentDrafts': 'Recent Drafts',
    'home.createNewDraft': 'Create a new draft',
    'home.creating': 'Creating...',
    'home.newDraft': '+ New Draft',
    'home.couldNotLoadDrafts': 'Could not load drafts',
    'home.noDraftsYet': 'No drafts yet.',
    'home.writingProjects': 'Writing Projects',
    'home.projectFilters': 'Project filters',
    'home.filter.active': 'Active',
    'home.filter.archived': 'Archived',
    'home.filter.all': 'All',
    'home.showActiveProjects': 'Show active projects',
    'home.showArchivedProjects': 'Show archived projects',
    'home.showAllProjects': 'Show all projects',
    'home.createNewProject': 'Create a new project',
    'home.newProject': '+ New Project',
    'home.projects': 'Projects',
    'home.couldNotLoadProjects': 'Could not load projects',
    'home.noArchivedProjects': 'No archived projects yet.',
    'home.noProjects': 'You do not have any projects here.',
    'home.createStory': 'Create Story',
    'home.pickScaffold': 'Pick a Scaffold',
    'home.createStoryHint': 'Choose a narrative scaffold, answer the questionnaire, and generate a draft to start writing.',
    'home.storyScaffolds': 'Narrative Scaffolds',
    'home.createNewScaffold': 'Create a new scaffold',
    'home.newScaffold': '+ New Scaffold',
    'home.couldNotLoadScaffolds': 'Could not load scaffolds',
    'home.noScaffoldsYet': 'No scaffolds yet.',
    'home.openScaffold': 'Open scaffold {title}',
    'home.untitledScaffold': 'Untitled scaffold',
    'home.storyScaffold': 'Story scaffold',
    'home.published': 'Published',
    'home.draft': 'Draft',

    'profile.tab.profile': 'Profile',
    'profile.tab.writing': 'Writing',
    'profile.tab.branding': 'Branding',
    'profile.tab.feedPreview': 'Public Feed Preview',
    'profile.tab.subscribers': 'Subscribers',
    'profile.publicUrl': 'Public URL',
    'profile.generateQr': 'Generate QR',
    'profile.liveUrl': 'Live URL',
    'profile.resetToMyUrl': 'Reset to my URL',
    'profile.embedOnWebsite': 'Embed on your website',
    'profile.copied': 'Copied!',
    'profile.copyCode': 'Copy code',
    'profile.feedPreviewHint': 'Enter a public profile URL to see the live feed. Dummy preview below shows the layout.',
    'profile.loadingSubscribers': 'Loading subscribers...',
    'profile.noSubscribers': 'No subscribers yet.',
    'profile.logout': 'Logout',
    'profile.dangerZone': 'Danger Zone',
    'profile.dangerText': 'Permanently delete your account, projects, and all content. This cannot be undone.',
    'profile.deleteAccount': 'Delete account',

    'profileForm.email': 'Email',
    'profileForm.role': 'Role',
    'profileForm.publisher': 'Publisher',
    'profileForm.user': 'User',
    'profileForm.savingRoleView': 'Saving role view...',
    'profileForm.roleViewSaved': 'Role view saved',
    'profileForm.couldNotSaveRoleView': 'Could not save role view',
    'profileForm.name': 'Name',
    'profileForm.namePlaceholder': 'Your name',
    'profileForm.authorBio': 'Author Bio',
    'profileForm.authorBioPlaceholder': 'Tell readers who you are and what you write.',
    'profileForm.phoneNumber': 'Phone Number',
    'profileForm.theme': 'Theme',
    'profileForm.themeSystem': 'System',
    'profileForm.themeLight': 'Light',
    'profileForm.themeDark': 'Dark',
    'profileForm.language': 'Language',
    'profileForm.languageEnglish': 'English',
    'profileForm.languageNorwegian': 'Norwegian',
    'profileForm.languageGerman': 'German',
    'profileForm.languageFrench': 'French',
    'profileForm.languageSpanish': 'Spanish',
    'profileForm.languageRomanian': 'Romanian',
    'profileForm.emailMarketing': 'Receive product updates and marketing emails',

    'register.accountType': 'Account type',
    'register.publisherBio': 'Publisher bio',
    'register.roleUser': 'User',
    'register.rolePublisher': 'Publisher',
    'register.publisherBioPlaceholder': 'Tell us what you publish and who you write for.',
    'register.publisherBioHint': 'This is shown to admins when reviewing your publisher request.',

    'scaffoldEditor.systemScaffoldAriaLabel': 'Scaffold system prompt',
    'scaffoldEditor.toggleSystemScaffold': 'Toggle system scaffold section',
    'scaffoldEditor.systemScaffoldTitle': 'System Scaffold',
    'scaffoldEditor.systemScaffoldInfoLabel': 'System scaffold explanation',
    'scaffoldEditor.systemScaffoldInfo': 'The system scaffold is the prompt template used to generate a draft from questionnaire answers and scaffold variables.',
    'scaffoldEditor.promptTemplateLabel': 'Prompt template used for draft generation',
    'scaffoldEditor.availablePlaceholders': 'Available placeholders',
    'scaffoldEditor.runtimeAnswersExplainer': '{{answersJson}} is injected automatically at runtime from questionnaire responses.',
    'scaffoldEditor.questionAnswerPlaceholderExplainer': 'Use {{qN}} to reference a specific question and {{rN}} to reference its answer (for example N=1,2,3...). Shape the generated story by changing question wording, question order, and answer detail; the model follows those values when building the draft.',
    'scaffoldEditor.taskComplexityAriaLabel': 'Task complexity guidance',
    'scaffoldEditor.taskComplexityTitle': 'Task Complexity',
    'scaffoldEditor.taskComplexityInfoLabel': 'Task complexity explanation',
    'scaffoldEditor.taskComplexityInfo': 'Controls how difficult generated writing tasks are when the draft is analysed. Choose Easy for quick wins, Medium for balanced revision work, and Difficult for deeper structural rewrites.',
    'scaffoldEditor.chooseDifficultyLevel': 'Choose difficulty level',
    'scaffoldEditor.taskComplexitySelectTitle': 'Set default task difficulty for generated task lists.',
    'scaffoldEditor.taskComplexity.easy': 'Easy',
    'scaffoldEditor.taskComplexity.medium': 'Medium',
    'scaffoldEditor.taskComplexity.difficult': 'Difficult',

    'scaffoldSim.panelTitle': 'Simulate your scaffold',
    'scaffoldSim.newButton': 'New',
    'scaffoldSim.closeButton': 'Close',
    'scaffoldSim.savedSimulations': 'Saved simulations',
    'scaffoldSim.loadingHistory': 'Loading history...',
    'scaffoldSim.noSimulations': 'No simulations yet.',
    'scaffoldSim.questionnaireSession': 'Questionnaire session',
    'scaffoldSim.delete': 'Delete',
    'scaffoldSim.deleteConfirm': 'Delete this simulation? This cannot be undone.',
    'scaffoldSim.startingQuestionnaire': 'Starting your questionnaire...',
    'scaffoldSim.startError': 'Could not start the questionnaire. Please try again.',
    'scaffoldSim.questionOf': 'Question {current} of {total}',
    'scaffoldSim.progressDone': '{total} of {total}',
    'scaffoldSim.llmCost': 'LLM cost: {used} / {cap} figs',
    'scaffoldSim.llmCapReached': '(cap reached)',
    'scaffoldSim.generatingInChat': 'Generating story in chat...',
    'scaffoldSim.generatedSaved': 'Story generated and saved in this simulation.',
    'scaffoldSim.generatedStory': 'Generated Story',
    'scaffoldSim.generatedTasks': '10 Generated Tasks',
    'scaffoldSim.noTasks': 'No tasks found for this simulation yet.',
    'scaffoldSim.suggestionLabel': 'Suggestion:',
    'scaffoldSim.answerPlaceholder': 'Your answer...',
    'scaffoldSim.autoSuggestEachQuestion': 'Auto-suggest each question',
    'scaffoldSim.suggestAnswer': 'Suggest answer',
    'scaffoldSim.suggesting': 'Suggesting...',
    'scaffoldSim.approveAllWithAi': 'Approve all with AI',
    'scaffoldSim.stopAutoApprove': 'Stop auto-approve',
    'scaffoldSim.approveAndNext': 'Approve and Next',
    'scaffoldSim.next': 'Next',
    'scaffoldSim.done': 'Done',
    'scaffoldSim.send': 'Send',
    'scaffoldSim.generateStory': 'Generate story',
    'scaffoldSim.generating': 'Generating...',
  },
  no: {
    'home.recentDrafts': 'Nylige utkast',
    'home.createNewDraft': 'Opprett nytt utkast',
    'home.creating': 'Oppretter...',
    'home.newDraft': '+ Nytt utkast',
    'home.couldNotLoadDrafts': 'Kunne ikke laste utkast',
    'home.noDraftsYet': 'Ingen utkast ennå.',
    'home.writingProjects': 'Skriveprosjekter',
    'home.projectFilters': 'Prosjektfiltre',
    'home.filter.active': 'Aktive',
    'home.filter.archived': 'Arkivert',
    'home.filter.all': 'Alle',
    'home.showActiveProjects': 'Vis aktive prosjekter',
    'home.showArchivedProjects': 'Vis arkiverte prosjekter',
    'home.showAllProjects': 'Vis alle prosjekter',
    'home.createNewProject': 'Opprett nytt prosjekt',
    'home.newProject': '+ Nytt prosjekt',
    'home.projects': 'Prosjekter',
    'home.couldNotLoadProjects': 'Kunne ikke laste prosjekter',
    'home.noArchivedProjects': 'Ingen arkiverte prosjekter ennå.',
    'home.noProjects': 'Du har ingen prosjekter her.',
    'home.createStory': 'Lag historie',
    'home.pickScaffold': 'Velg stillas',
    'home.createStoryHint': 'Velg et narrativt stillas, svar pa sporskjemaet og generer et utkast for a starte skrivingen.',
    'home.storyScaffolds': 'Historiestillaser',
    'home.createNewScaffold': 'Opprett nytt stillas',
    'home.newScaffold': '+ Nytt stillas',
    'home.couldNotLoadScaffolds': 'Kunne ikke laste stillaser',
    'home.noScaffoldsYet': 'Ingen stillaser ennå.',
    'home.openScaffold': 'Apne stillas {title}',
    'home.untitledScaffold': 'Uten tittel',
    'home.storyScaffold': 'Historiestillas',
    'home.published': 'Publisert',
    'home.draft': 'Utkast',

    'profile.tab.profile': 'Profil',
    'profile.tab.writing': 'Skriving',
    'profile.tab.branding': 'Profilering',
    'profile.tab.feedPreview': 'Forhandsvisning av offentlig feed',
    'profile.tab.subscribers': 'Abonnenter',
    'profile.publicUrl': 'Offentlig URL',
    'profile.generateQr': 'Generer QR',
    'profile.liveUrl': 'Live URL',
    'profile.resetToMyUrl': 'Tilbakestill til min URL',
    'profile.embedOnWebsite': 'Bygg inn pa nettsiden din',
    'profile.copied': 'Kopiert!',
    'profile.copyCode': 'Kopier kode',
    'profile.feedPreviewHint': 'Skriv inn en offentlig profil-URL for a se live feed. Forhandsvisningen under viser layouten.',
    'profile.loadingSubscribers': 'Laster abonnenter...',
    'profile.noSubscribers': 'Ingen abonnenter ennå.',
    'profile.logout': 'Logg ut',
    'profile.dangerZone': 'Faresone',
    'profile.dangerText': 'Slett kontoen din, prosjekter og alt innhold permanent. Dette kan ikke angres.',
    'profile.deleteAccount': 'Slett konto',

    'profileForm.email': 'E-post',
    'profileForm.role': 'Rolle',
    'profileForm.publisher': 'Utgiver',
    'profileForm.user': 'Bruker',
    'profileForm.savingRoleView': 'Lagrer rollevisning...',
    'profileForm.roleViewSaved': 'Rollevisning lagret',
    'profileForm.couldNotSaveRoleView': 'Kunne ikke lagre rollevisning',
    'profileForm.name': 'Navn',
    'profileForm.namePlaceholder': 'Navnet ditt',
    'profileForm.authorBio': 'Forfatterbio',
    'profileForm.authorBioPlaceholder': 'Fortell leserne hvem du er og hva du skriver.',
    'profileForm.phoneNumber': 'Telefonnummer',
    'profileForm.theme': 'Tema',
    'profileForm.themeSystem': 'System',
    'profileForm.themeLight': 'Lyst',
    'profileForm.themeDark': 'Morkt',
    'profileForm.language': 'Sprak',
    'profileForm.languageEnglish': 'Engelsk',
    'profileForm.languageNorwegian': 'Norsk',
    'profileForm.languageGerman': 'Tysk',
    'profileForm.languageFrench': 'Fransk',
    'profileForm.languageSpanish': 'Spansk',
    'profileForm.languageRomanian': 'Rumensk',
    'profileForm.emailMarketing': 'Motta produktoppdateringer og markedsforings-e-poster',

    'register.accountType': 'Kontotype',
    'register.publisherBio': 'Utgiverbio',
    'register.roleUser': 'Bruker',
    'register.rolePublisher': 'Utgiver',
    'register.publisherBioPlaceholder': 'Fortell oss hva du publiserer og hvem du skriver for.',
    'register.publisherBioHint': 'Dette vises til administratorer nar de vurderer utgiverforesporselen din.',

    'scaffoldEditor.systemScaffoldAriaLabel': 'Systemprompt for stillas',
    'scaffoldEditor.toggleSystemScaffold': 'Veksle systemstillas-seksjon',
    'scaffoldEditor.systemScaffoldTitle': 'Systemstillas',
    'scaffoldEditor.systemScaffoldInfoLabel': 'Forklaring av systemstillas',
    'scaffoldEditor.systemScaffoldInfo': 'Systemstillaset er promptmalen som brukes til a generere et utkast fra sporskjema-svar og stillasvariabler.',
    'scaffoldEditor.promptTemplateLabel': 'Promptmal brukt for utkastgenerering',
    'scaffoldEditor.availablePlaceholders': 'Tilgjengelige plassholdere',
    'scaffoldEditor.runtimeAnswersExplainer': '{{answersJson}} fylles automatisk ved kjoring fra svar i sporskjemaet.',
    'scaffoldEditor.questionAnswerPlaceholderExplainer': 'Bruk {{qN}} for a referere til et spesifikt sporsmal og {{rN}} for a referere til svaret (for eksempel N=1,2,3...). Form den genererte historien ved a endre sporsmalsordlyd, sporsmalsrekkefolge og detaljniva i svarene; modellen folger disse verdiene nar den bygger utkastet.',
    'scaffoldEditor.taskComplexityAriaLabel': 'Veiledning for oppgavekompleksitet',
    'scaffoldEditor.taskComplexityTitle': 'Oppgavekompleksitet',
    'scaffoldEditor.taskComplexityInfoLabel': 'Forklaring av oppgavekompleksitet',
    'scaffoldEditor.taskComplexityInfo': 'Styrer hvor vanskelige de genererte skriveoppgavene er nar utkastet analyseres. Velg Easy for raske gevinster, Medium for balansert revisjonsarbeid og Difficult for dypere strukturelle omskrivinger.',
    'scaffoldEditor.chooseDifficultyLevel': 'Velg vanskelighetsniva',
    'scaffoldEditor.taskComplexitySelectTitle': 'Sett standard vanskelighetsniva for genererte oppgavelister.',
    'scaffoldEditor.taskComplexity.easy': 'Easy',
    'scaffoldEditor.taskComplexity.medium': 'Medium',
    'scaffoldEditor.taskComplexity.difficult': 'Difficult',

    'scaffoldSim.panelTitle': 'Simuler stillaset ditt',
    'scaffoldSim.newButton': 'Ny',
    'scaffoldSim.closeButton': 'Lukk',
    'scaffoldSim.savedSimulations': 'Lagrede simuleringer',
    'scaffoldSim.loadingHistory': 'Laster historikk...',
    'scaffoldSim.noSimulations': 'Ingen simuleringer ennå.',
    'scaffoldSim.questionnaireSession': 'Sporskjema-okt',
    'scaffoldSim.delete': 'Slett',
    'scaffoldSim.deleteConfirm': 'Slette denne simuleringen? Dette kan ikke angres.',
    'scaffoldSim.startingQuestionnaire': 'Starter sporskjemaet ditt...',
    'scaffoldSim.startError': 'Kunne ikke starte sporskjemaet. Prov igjen.',
    'scaffoldSim.questionOf': 'Sporsmal {current} av {total}',
    'scaffoldSim.progressDone': '{total} av {total}',
    'scaffoldSim.llmCost': 'LLM-kostnad: {used} / {cap} figs',
    'scaffoldSim.llmCapReached': '(tak nadd)',
    'scaffoldSim.generatingInChat': 'Genererer historie i chat...',
    'scaffoldSim.generatedSaved': 'Historien er generert og lagret i denne simuleringen.',
    'scaffoldSim.generatedStory': 'Generert historie',
    'scaffoldSim.generatedTasks': '10 genererte oppgaver',
    'scaffoldSim.noTasks': 'Ingen oppgaver funnet for denne simuleringen ennå.',
    'scaffoldSim.suggestionLabel': 'Forslag:',
    'scaffoldSim.answerPlaceholder': 'Ditt svar...',
    'scaffoldSim.autoSuggestEachQuestion': 'Auto-foresla hvert sporsmal',
    'scaffoldSim.suggestAnswer': 'Foresla svar',
    'scaffoldSim.suggesting': 'Foreslar...',
    'scaffoldSim.approveAllWithAi': 'Godkjenn alle med AI',
    'scaffoldSim.stopAutoApprove': 'Stopp auto-godkjenning',
    'scaffoldSim.approveAndNext': 'Godkjenn og neste',
    'scaffoldSim.next': 'Neste',
    'scaffoldSim.done': 'Ferdig',
    'scaffoldSim.send': 'Send',
    'scaffoldSim.generateStory': 'Generer historie',
    'scaffoldSim.generating': 'Genererer...',
  },
};

function format(template, vars = {}) {
  return String(template).replace(/\{(\w+)\}/g, (_, key) => String(vars[key] ?? `{${key}}`));
}

export function getWebLanguage() {
  const prefs = loadUserPrefs();
  const language = String(prefs?.language || 'en');
  return SUPPORTED.has(language) ? language : 'en';
}

export function tWeb(languageCode, key, vars) {
  const language = SUPPORTED.has(String(languageCode || '')) ? String(languageCode) : 'en';
  const template = TRANSLATIONS[language]?.[key] || TRANSLATIONS.en[key] || key;
  return format(template, vars);
}
